import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { ArrowLeft, ScanLine, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { feedback, peekQr, getPos, unlockSound } from "@/lib/qr";
import { useAuth } from "@/context/AuthContext";
import { ScanResult, AmountPicker } from "@/components/qr/ScanResult";

function AmountStep({ onNext }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [pts, setPts] = useState(10);
  const [msg, setMsg] = useState("");
  const bal = Number(user?.points || 0);
  const short = Number(pts) > bal;
  const go = () => { const n = parseInt(pts, 10); if (n > 0) { unlockSound(); onNext({ pts: n, msg }); } };
  return (
    <div className="bg-white rounded-3xl p-4 border border-slate-100 space-y-2" data-testid="scan-amount-step">
      <div className="font-bold text-slate-900">1. Sa kredite do të dërgosh?</div>
      <AmountPicker pts={pts} setPts={setPts} msg={msg} setMsg={setMsg} />
      <div className="text-xs text-slate-500">Ke {Math.round(bal * 100) / 100} kredite</div>
      {short ? <>
        <div data-testid="scan-amount-short" className="rounded-xl bg-rose-50 border border-rose-200 p-2.5 text-sm font-semibold text-rose-700">🔴 Nuk keni GjejKredit të mjaftueshme. Ju duhen edhe {Math.round((Number(pts) - bal) * 100) / 100} kredite.</div>
        <button data-testid="scan-amount-buy" onClick={() => nav("/gjejkredit/bli")} className="w-full h-12 rounded-xl bg-amber-500 text-white font-bold">Bli GjejKredit</button>
      </> :
      <button data-testid="scan-amount-next" onClick={go} className="w-full h-12 rounded-xl bg-emerald-500 text-white font-bold flex items-center justify-center gap-2"><ScanLine className="w-5 h-5" />2. Skano QR-në e personit</button>}
    </div>
  );
}

function Camera({ onCode }) {
  const [err, setErr] = useState("");
  useEffect(() => {
    const s = new Html5Qrcode("qr-reader", { verbose: false });
    let done = false;
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 230, height: 230 } }, (text) => {
      if (done) return;
      done = true;
      s.stop().catch(() => {}).finally(() => onCode(text));
    }, () => {}).catch(() => setErr("Lejo kamerën për të skanuar QR-në."));
    return () => { done = true; if (s.isScanning) s.stop().catch(() => {}); };
  }, [onCode]);
  return (
    <div className="rounded-3xl overflow-hidden bg-slate-900 relative" data-testid="qr-camera">
      <div id="qr-reader" className="w-full min-h-[300px]" />
      {err && <p className="absolute inset-0 flex items-center justify-center text-white text-sm p-6 text-center" data-testid="qr-camera-error">{err}</p>}
      <p className="text-center text-white/80 text-sm py-3">Vendosni QR kodin brenda kornizës</p>
    </div>
  );
}

export default function ScanPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user, requireAuth } = useAuth();
  const [state, setState] = useState({ phase: sp.get("send") ? "amount" : "scan" });
  const [preset, setPreset] = useState(null);
  const started = useRef(false);
  const handle = useCallback(async (raw) => {
    const { code, t } = peekQr(raw);
    setState({ phase: "busy", msg: t === "b" ? "Po kontrollojmë lokacionin..." : "Duke verifikuar..." });
    const pos = t === "b" ? await getPos() : {};
    try { const { data } = await api.post("/qr/scan", { code, ...pos }); feedback(true); setState({ phase: "result", r: data }); }
    catch (e) { feedback(false); setState({ phase: "error", msg: errMsg(e) }); }
  }, []);
  useEffect(() => {
    const c = sp.get("c");
    if (!c || started.current || user === null) return;
    if (!user) { requireAuth?.(); return; }
    started.current = true;
    handle(c);
  }, [sp, user, handle, requireAuth]);
  const again = () => { unlockSound(); setPreset(null); setState({ phase: sp.get("send") ? "amount" : "scan" }); };
  if (user === false) return <div className="p-8 text-center text-slate-500">Hyr në llogari për të skanuar.</div>;
  return (
    <div className="px-4 pt-3 pb-28 space-y-3" data-testid="scan-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-slate-700 font-semibold" data-testid="scan-back"><ArrowLeft className="w-5 h-5" />Skanoni QR kodin</button>
      {state.phase === "amount" && <AmountStep onNext={(v) => { setPreset(v); setState({ phase: "scan" }); }} />}
      {state.phase === "scan" && preset && <div className="text-center text-sm font-semibold text-emerald-700" data-testid="scan-preset">Do të dërgosh {preset.pts} kredite · skano QR-në e personit</div>}
      {state.phase === "scan" && !sp.get("c") && <Camera onCode={handle} />}
      {state.phase === "busy" && <div className="py-16 text-center text-slate-500" data-testid="scan-busy">{state.msg}</div>}
      {state.phase === "error" && (
        <div className="bg-rose-50 rounded-3xl p-6 text-center" data-testid="scan-error">
          <XCircle className="w-16 h-16 text-rose-500 mx-auto" /><div className="font-bold text-rose-700 mt-2">{state.msg}</div>
        </div>
      )}
      {state.phase === "result" && <ScanResult r={state.r} preset={preset} />}
      {(state.phase === "error" || state.phase === "result" || sp.get("c")) && state.phase !== "busy" && (
        <button data-testid="scan-again" onClick={() => { if (sp.get("c")) nav("/skano", { replace: true }); again(); }} className="w-full h-12 rounded-2xl bg-slate-900 text-white font-bold flex items-center justify-center gap-2"><ScanLine className="w-5 h-5" />Skano përsëri</button>
      )}
    </div>
  );
}
